import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import CaptionImage from "../components/CaptionImage";
import { Reveal, MaskedLines } from "../components/Motion";
import { Field, SubmitButton, useSubmission } from "../components/Forms";
import { IMAGES } from "../site/content";
import { Briefcase, GraduationCap, HeartHandshake, HandHelping, Users } from "lucide-react";

const INTENTS = [
  { id: "Hire", label: "Hire — services for my business", icon: Briefcase },
  { id: "Learn", label: "Learn — courses and tuition", icon: GraduationCap },
  { id: "Donate", label: "Donate — support the nonprofit", icon: HeartHandshake },
  { id: "Volunteer", label: "Volunteer — give skills and time", icon: HandHelping },
  { id: "Partner", label: "Partner — work alongside us", icon: Users },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initial = INTENTS.some((i) => i.id === params.get("intent")) ? params.get("intent") : "Hire";
  const { sending, sent, send } = useSubmission("contact");
  const [form, setForm] = useState({ name: "", email: "", phone: "", intent: initial, message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <Seo
        title="Contact — Hire, Learn, Donate, Volunteer, Partner"
        description="One form, five doors. Hire ProOne for services, start learning, donate, volunteer, or partner with the rural work. A human replies to every message."
        path="/contact"
      />

      <section className="pt-28 sm:pt-36 pb-16 sm:pb-24" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="eyebrow flex items-center gap-3">
            <span className="h-px w-8 bg-teal" aria-hidden="true" />
            Contact
          </p>
          <MaskedLines
            lines={["One form.", <span key="t" className="italic text-teal">Five doors.</span>]}
            className="mt-6 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-charcoal"
          />
        </div>
      </section>

      <section className="pb-20 sm:pb-28" data-testid="contact-body">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              {sent ? (
                <div className="rounded-3xl border border-teal/30 bg-teal/5 p-10 text-center" data-testid="contact-success">
                  <p className="font-serif text-3xl text-teal">Message received.</p>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-charcoal-muted">
                    Thank you for writing. A real person — not an autoresponder — will
                    reply. Services enquiries get a free consultation; giving enquiries
                    get a human answer.
                  </p>
                </div>
              ) : (
                <Reveal>
                  <form
                    className="rounded-3xl border border-line bg-white/70 p-6 sm:p-9"
                    data-testid="contact-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      send(form);
                    }}
                  >
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Name" htmlFor="contact-name">
                        <input id="contact-name" required className="field" value={form.name} onChange={set("name")} data-testid="contact-name-input" placeholder="First name is fine" />
                      </Field>
                      <Field label="Email" htmlFor="contact-email">
                        <input id="contact-email" required type="email" className="field" value={form.email} onChange={set("email")} data-testid="contact-email-input" placeholder="you@example.com" />
                      </Field>
                    </div>
                    <div className="mt-5 grid gap-5 sm:grid-cols-2">
                      <Field label="Phone (optional)" htmlFor="contact-phone">
                        <input id="contact-phone" type="tel" className="field" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" placeholder="With country code" />
                      </Field>
                      <Field label="I want to" htmlFor="contact-intent">
                        <select id="contact-intent" className="field" value={form.intent} onChange={set("intent")} data-testid="contact-intent-select">
                          {INTENTS.map((i) => (
                            <option key={i.id} value={i.id}>{i.label}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <div className="mt-5">
                      <Field label="Message" htmlFor="contact-message">
                        <textarea id="contact-message" required rows={6} className="field" value={form.message} onChange={set("message")} data-testid="contact-message-input" placeholder="Tell us what you need, in plain words. We read everything." />
                      </Field>
                    </div>
                    <div className="mt-7">
                      <SubmitButton sending={sending} testid="contact-submit-button">Send the message</SubmitButton>
                    </div>
                  </form>
                </Reveal>
              )}
            </div>
            <div className="lg:col-span-5 space-y-8">
              <Reveal delay={0.1}>
                <CaptionImage image={IMAGES.contactPhone} ratio="aspect-[4/3]" rounded="rounded-[2rem]" />
              </Reveal>
              <Reveal delay={0.15}>
                <div className="space-y-4">
                  <div className="rounded-2xl border border-line bg-sand/60 p-6" data-testid="promise-services">
                    <p className="font-serif text-lg font-semibold text-charcoal">Hiring?</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-charcoal-muted">
                      Every services enquiry includes a free consultation — scope, timeline,
                      and price before you commit to anything.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-line bg-sand/60 p-6" data-testid="promise-giving">
                    <p className="font-serif text-lg font-semibold text-charcoal">Giving?</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-charcoal-muted">
                      A human replies to every message about donations, in-kind gifts, and
                      volunteering. Always.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
